import React, { useEffect, useState, useCallback } from "react";
import Widget from "../../../components/Elements/Widget";
import { PlantProfile, Field, DataSourceRasterLayer, LayerVisibility } from "../../../types/types";
import { Form, Button } from "react-bootstrap";
import { useStore } from "../../../providers/AppProvider";
import { SETTING_SEASON_YEAR } from "../../../utils/settings";
import { queryDwPointCanopyPropertyData, queryDwPointCanopyStatsData, queryDwPointBloomData, queryDwPointSoilStatsData, queryDwPointSoilStatsData2025, queryDwPointSoilDRLData, queryDwPointCultivarStatus } from "../../../utils/dwHelpers";
import { Definition } from "../../../../../data/Definition";
import AboveWidget3SparkCell from "./AboveWidget3SparkCell";
import AboveWidgetImagery from "./AboveWidgetImagery";
import { isAbortError } from "../../../../../utils/errorUtils";
import AboveWidgetSoilFertilityCell from "./AboveWidgetSoilFertilityCell";
import AboveWidgetSoilWaterCell from "./AboveWidgetSoilWaterCell";
import { useParams } from 'react-router-dom';
import { MapLayer } from "../../../deckgl/MapLayer";
import { useUnitConversion, formatUnitAbbreviation} from '../../../utils/unitConversion';
import _ from "lodash";
import { l2BBConfig } from '../../../utils/settings';

type Props = {
    plantProfile: PlantProfile | null;
    selectedField: Field | null;
    propertyUnitsData: any | null;
    rasterDataSources: DataSourceRasterLayer[];
    bbCardDataSources: DataSourceRasterLayer[];
    selectedPlantObj: any | null;
    bbCardDataCounter: number;
    setBbCardDataCounter: Function;
    dateArrayIndexSelected: number;
    setDateArrayIndexSelected: Function;
    dateArrayDateStringSelected: string;
    mapLayers: MapLayer[];
    dateSelected: string | null;
    dwBBCardMinMaxData: any;
};

const AboveWidget = ({dateSelected, mapLayers, plantProfile, selectedField, 
    propertyUnitsData, rasterDataSources, bbCardDataSources, bbCardDataCounter,
     setBbCardDataCounter, selectedPlantObj, dateArrayIndexSelected, setDateArrayIndexSelected, 
     dateArrayDateStringSelected, dwBBCardMinMaxData }: Props) => {
    const { state, loading, settings } = useStore();
    const { vp, field } = useParams();
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [data, setData] = useState<any | null>(null);
    const [definitionState, setDefinitionState] = useState<Definition | null>(null);

    const [dataCanopyArea, setDataCanopyArea] = useState<any | null>(null);
    const [dataBloom, setDataBloom] = useState<any | null>(null);
    const [dataCanopyVolume, setDataCanopyVolume] = useState<any | null>(null);
    const [dataCPI, setDataCPI] = useState<any | null>(null);
    const [dataCanopyHeight, setDataCanopyHeight] = useState<any | null>(null);

    const [dataCanopyVVI, setDataCanopyVVI] = useState<any | null>(null);
    const [dataCanopyPGI, setDataCanopyPGI] = useState<any | null>(null);
    const [dataCanopyTIR, setDataCanopyTIR] = useState<any | null>(null);

    const [dataSoilStats, setDataSoilStats] = useState<any | null>(null);
    const [dataSoilStats2025, setDataSoilStats2025] = useState<any | null>(null);
    const [dataSoilDRLStats, setDataSoilDRLStats] = useState<any | null>(null);
    const [dataCultivarStatus, setDataCultivarStatus] = useState<any | null>(null);
    const [bbCardByCategory, setBbCardByCategory] = useState<any | null>(null);
    const [bottomCellOptions2025, setBottomCellOptions2025] = useState<any | null>(null);

    const [loadCount, setLoadCount] = useState<number>(0);
    const [selectedTopCell, setSelectedTopCell] = useState<string>("Canopy");
    const [selectedBottomCell, setSelectedBottomCell] = useState<string>("SoilWater");

    const { localePreferences } = useStore();
    const { convertToPreferredUnitValue, convertToPreferredUnitString, unitsConvert } = useUnitConversion();
    
    const topCellOptions = [
        { id: "Canopy", name: "Canopy", description: "Canopy" },
        { id: "CanopyHealth", name: "Canopy Health", description: "Canopy Health" },
        { id: "CanopySize", name: "Canopy Size", description: "Canopy Size" },
        { id: "BloomIndex", name: "Bloom Index", description: "Bloom Index" },
    ];

    const bottomCellOptions = [
        { id: "SoilWater", name: "Soil Water", description: "...description for Soil Water" },
        { id: "SoilFertility", name: "Soil Fertility", description: "...description for Soil Fertility" },
        { id: "SoilMicronutrients", name: "Soil Micronutrients", description: "...description for Soil Micronutrients" },
        { id: "SoilCationRatios", name: "Soil Cation Ratios", description: "...description for Soil Cation Ratios" },
        { id: "SoilPaste", name: "Soil Paste", description: "...description for Soil Paste" },
        { id: "SoilPasteRatios", name: "Soil Paste Ratios", description: "...description for Soil Paste Ratios" },
        { id: "SoilHealth", name: "Soil Health", description: "...description for Soil Health" },
    ];
    /*
    Soil Water
    Soil Paste
    Soil Fertility
    Soil Micronutrients
    Readily Available Ions
    Soil Available Ions
    Soil Exchangeable Ions
    Soil Exchangeable Ratios
    Total Digestible Ions
    Soil Physical Properties
    Soil Health
    */
    // Group the nested l2BBConfig entries by their bbCard value so that bbCard becomes
    // the first-level categorization and each category lists the full nested JSON nodes.
    const { buckets: _bbBuckets, bottomCellOptions: _bottomOptions2025 } = React.useMemo((): { buckets: Record<string, any[]>; bottomCellOptions: { id: string; name: string; description: string }[] } => {
        // Build a 2025 bottom cell options list from the first-level keys of the grouped bbCard categories
        type BottomCellOption2025 = { id: string; name: string; description: string };

        const cardNames = new Set<string>();
        const seenForOptions = new WeakSet<object>();

        const walkForOptions = (node: any) => {
            if (node == null || seenForOptions.has(node)) return;

            if (Array.isArray(node)) {
                node.forEach(walkForOptions);
                return;
            }

            if (typeof node === 'object') {
                seenForOptions.add(node);

                const raw = (node as any).bbCard;
                const card = raw === "" ? "UNKNOWN" : raw;
                if (typeof card === 'string') {
                    cardNames.add(card);
                }

                Object.values(node).forEach(walkForOptions);
            }
        };

        walkForOptions(l2BBConfig);

        const _bottomCellOptions2025: BottomCellOption2025[] = Array.from(cardNames)
            .map((card) => ({
            id: String(card).replace(/\s+/g, ''),
            name: card,
            description: '2025 option',
            }))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

        const buckets: Record<string, any[]> = {};
        const seen = new WeakSet<object>();

        const walk = (node: any) => {
            console.log("A082525 node", node);
            if (node == null || seen.has(node)) return;

            if (Array.isArray(node)) {
                node.forEach(walk);
                return;
            }

            if (typeof node === 'object') {
                seen.add(node);

                let card = (node as any).bbCard == "" ? "UNKNOWN" : (node as any).bbCard;
                if (card != null) {
                    if (!buckets[card]) buckets[card] = [];
                    if (!buckets[card] || Array.isArray(buckets[card])) {
                        (buckets as any)[card] = {};
                    }
                    const key = String((node as any).ddProp ?? 'UNKNOWN');
                    (buckets as any)[card][key] = node;
                }

                Object.values(node).forEach(walk);
            }
        };

        walk(l2BBConfig);
        return { buckets, bottomCellOptions: _bottomCellOptions2025 };
    }, []);

    useEffect(() => {
        
        setBbCardByCategory(_bbBuckets);

        const HARDCODE_bottomOptions2025 = [
            { "id": "SoilWater", "name": "Soil Water", "description": "2025 option" }, 
            { "id": "SoilPaste", "name": "Soil Paste", "description": "2025 option" }, 
            { "id": "SoilFertility", "name": "Soil Fertility", "description": "2025 option" }, 
            { "id": "SoilMicronutrients", "name": "Soil Micronutrients", "description": "2025 option" }, 
            { "id": "ReadilyAvailableIons", "name": "Readily Available Ions", "description": "2025 option" }, 
            { "id": "SoilAvailableIons", "name": "Soil Available Ions", "description": "2025 option" }, 
            { "id": "SoilExchangeableIons", "name": "Soil Exchangeable Ions", "description": "2025 option" }, 
            { "id": "SoilExchangeableRatios", "name": "Soil Exchangeable Ratios", "description": "2025 option" }, 
            { "id": "TotalDigestibleIons", "name": "Total Digestible Ions", "description": "2025 option" },
            { "id": "SoilHealth", "name": "Soil Health", "description": "2025 option" }, 
            { "id": "SoilPhysicalProperties", "name": "Soil Physical Properties", "description": "2025 option" }, 
        ];

        console.log("A091025 HARDCODE_bottomOptions2025", HARDCODE_bottomOptions2025);
        console.log("A091025 _bbBuckets", _bbBuckets);
        console.log("A091025 dataSoilStats2025", dataSoilStats2025);

        // If buckets or stats aren’t ready yet, don’t filter—use hardcoded list to avoid undefined[0].name crashes.
        const bucketsReady = _bbBuckets && typeof _bbBuckets === 'object';
        const statsReady = Array.isArray(dataSoilStats2025) && dataSoilStats2025.length > 0;

        if (!bucketsReady || !statsReady) {
            //setBottomCellOptions2025(HARDCODE_bottomOptions2025);
            setBottomCellOptions2025([]);
            return; // <-- important so we don’t run the filtering below prematurely
        }

        // --- Helper: does any record in dataSoilStats2025 contain this "<ddProp>_mean" key with a non-null/undefined value?
        const hasMeanFieldInAnyRow = (fieldKey: string) => {
            return dataSoilStats2025.some((row: any) =>
            row && Object.prototype.hasOwnProperty.call(row, fieldKey) && row[fieldKey] !== null && row[fieldKey] !== undefined
            );
        };

        // --- For each hardcoded option, find its bucket by name, then check sub-keys for ddProp -> "<ddProp>_mean" in dataSoilStats2025.
        const filteredBottomOptions2025 = HARDCODE_bottomOptions2025.filter((opt) => {
            const bucket = _bbBuckets[opt.name];

            // If there is no matching bucket for this category, drop it.
            if (!bucket || typeof bucket !== 'object') {
            // console.debug(`[filter] No bucket for category "${opt.name}" -> removing`);
            return false;
            }

            const subKeys = Object.keys(bucket);
            if (subKeys.length === 0) {
            // console.debug(`[filter] Empty bucket for category "${opt.name}" -> removing`);
            return false;
            }

            // Keep the option if ANY subkey has a ddProp whose "<ddProp>_mean" exists in dataSoilStats2025.
            const hasAnySupportedProp = subKeys.some((k: any) => {
            const ddProp = bucket[k]?.ddProp;
            if (!ddProp || typeof ddProp !== 'string') return false;
            const meanKey = `${ddProp}_mean`;
            return hasMeanFieldInAnyRow(meanKey);
            });

            // console.debug(`[filter] Category "${opt.name}" -> ${hasAnySupportedProp ? 'KEEP' : 'REMOVE'}`);
            return hasAnySupportedProp;
        });

        // If filtering would remove everything (or something odd happened), fall back to hardcoded
        // to avoid downstream code trying to read `.name` off an undefined element.
        if (!filteredBottomOptions2025 || filteredBottomOptions2025.length === 0) {
            setBottomCellOptions2025(HARDCODE_bottomOptions2025);
        } else {
            setBottomCellOptions2025(filteredBottomOptions2025);
        }

        // To generate a new menu order or menu altogether, use _bottomOptions2025, console log
        // the results then copy and paste the JSON to const HARDCODE_bottomOptions2025
        // and modify as needed.
    }, [_bbBuckets, _bottomOptions2025, dataSoilStats2025]);

    console.log("A082225 bbCardByCategory", bbCardByCategory)
    console.log("A082225 bottomCellOptions2025", bottomCellOptions2025)
    useEffect(() => {
        console.log("AA092425 bbCardDataSources", bbCardDataSources)   
        console.log("AA092425 rasterDataSources", rasterDataSources)   
        console.log("AA092425 selectedPlantObj", selectedPlantObj)
        console.log("AA092425 selectedField", selectedField)
        console.log("C092425 dataCanopyArea", dataCanopyArea)
        console.log("AA092425 dataCanopyHeight", dataCanopyHeight)
        console.log("AA092425 dataCanopyVolume", dataCanopyVolume)
        console.log("AA092425 propertyUnitsData", propertyUnitsData)
        console.log("AA092425 dateArrayIndexSelected", dateArrayIndexSelected)
        console.log("AA092425 dateArrayDateStringSelected", dateArrayDateStringSelected)
        console.log("AA092425 dataCanopyVVI", dataCanopyVVI)
        console.log("AA092425 dataCanopyPGI", dataCanopyPGI)
        console.log("AA092425 dataCanopyTIR", dataCanopyTIR)
        console.log("AA092425 dataCPI", dataCPI)
        console.log("AA092425 mapLayers", mapLayers)
        //setRefreshIncrement(refreshIncrement + 1);
    }, [/*mapLayers, dataCPI, dataCanopyTIR, dataCanopyPGI, dataCanopyVVI, dateArrayDateStringSelected, dateArrayIndexSelected, propertyUnitsData, dataCanopyVolume, dataCanopyHeight,*/ dataCanopyArea, /*selectedField, selectedPlantObj, rasterDataSources, bbCardDataSources*/]);
    
    useEffect(() => {
        console.log("A092425 plantProfile changed;", plantProfile);
        if (loading || !plantProfile?.plantprofileId) {
            return;
        }

        const controller = new AbortController();

        let definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('plant'));
        if (!definition) {
            definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('tree'));
        }
        let definitionNames = state.definitions.map((d: Definition) => d.name.toLowerCase());
        if (definitionNames.includes('vine') || definitionNames.includes('tree')) {
            if (selectedField?.crop?.name.toLocaleLowerCase() === 'grapes') {
                definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('vine'));
            } else if (selectedField?.crop?.name.toLocaleLowerCase() === 'almonds') {
                definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('tree'));
            }
        }

        if (!definition) {
            console.error('Unable to find field definition (at A)');
            return;
        }
        setDefinitionState(definition);
        setDataLoading(true);
        console.log("A092425 definition, settings[SETTING_SEASON_YEAR], plantProfile.plantprofileId, controller.signal", definition, settings[SETTING_SEASON_YEAR], plantProfile.plantprofileId, controller.signal);
        
        // Execute all data loading operations in parallel and wait for completion
        const loadAllData = async () => {
            try {
                const year = settings[SETTING_SEASON_YEAR];
                const plantId = plantProfile.plantprofileId;
                
                if (year && plantId && definition) {
                    await Promise.all([
                        _loadPropertyData(year, plantId, 'vvic', definition, controller.signal),
                        _loadCanopyStatData(year, plantId, 'vvic', definition, controller.signal),
                        _loadSoilStatData(year, plantId, 'vvic', definition, controller.signal),
                        _loadBloomData(year, plantId, 'vvic', definition, controller.signal)
                    ]);
                    console.log("A092425 All data loading operations completed");
                }
                setDataLoading(false);
            } catch (error) {
                if (!isAbortError(error)) {
                    console.error('A092425 One or more data loading operations failed:', error);
                }
                setDataLoading(false);
            }
        };
        
        loadAllData();

        return () => {
            controller.abort();
        };
    }, [plantProfile]);

    useEffect(() => {
        if (dataCanopyHeight) {
            let selectedDateIndex: number = dataCanopyHeight[3].findIndex((date: string) => date.startsWith(dateArrayDateStringSelected));
            setDateArrayIndexSelected(selectedDateIndex);
        }
    }, [dateArrayDateStringSelected, dataCanopyHeight]);

    useEffect(() => {
        if(dataCanopyHeight){
            console.log("B053024 dateArrayDateStringSelected", dateArrayDateStringSelected);
            console.log("B053024 dataCanopyArea", dataCanopyArea)
            console.log("B053024 dataCanopyHeight", dataCanopyHeight)
            console.log("B053024 dataCanopyVolume", dataCanopyVolume)
            console.log("B053024 dataCanopyVVI", dataCanopyVVI)
            console.log("B053024 dataCanopyPGI", dataCanopyPGI)
            console.log("B053024 dataCanopyTIR", dataCanopyTIR)
            let selectedDateIndex: number = dataCanopyHeight[3].findIndex((date: string) => date.startsWith(dateArrayDateStringSelected));
            console.log("B053024 selectedDateIndex", selectedDateIndex);
            setDateArrayIndexSelected(selectedDateIndex);
        }
    }, [dateArrayDateStringSelected,dataCanopyHeight]);

    const onTopCellTypeChange = (e: any) => {
        setSelectedTopCell(e.target.value);
    };
    const onBottomCellTypeChange = (e: any) => {
        setSelectedBottomCell(e.target.value);
    };

    const _loadPropertyData = async (year: number, _plantprofileId: number, metric: string, definition: Definition, signal: AbortSignal) => {
        const dtStart = `${year}-01-01 00:00:00.00`;
        const dtEnd = `${year}-12-31 23:59:59.59`;
        try {
            const data = await queryDwPointCanopyPropertyData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            console.log("C092425 _loadPropertyData data",data);
            console.log("C092425 _loadPropertyData data.length",data.length);
            if (data && data.length) {
                console.log("C092425 setDataCanopyArea( data",data);
                console.log("C092425 setDataCanopyArea( dwBBCardMinMaxData",dwBBCardMinMaxData);
                setDataCanopyArea([
                    "Canopy Area",
                    "Canopy Area",
                    data.map((d: any) => d.CanopyArea_value),
                    data.map((d: any) => d.CanopyArea_time ? d.CanopyArea_time : d.CanopyArea_date),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinNumber[0].CanopyArea_min,
                    dwBBCardMinMaxData.dataMaxNumber[0].CanopyArea_max,
                ]);

                setDataCanopyVolume([
                    "Canopy Volume",
                    "Canopy Volume",
                    data.map((d: any) => d.CanopyVolume_value),
                    data.map((d: any) => d.CanopyArea_time ? d.CanopyArea_time : d.CanopyArea_date),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinNumber[0].CanopyVolume_min,
                    dwBBCardMinMaxData.dataMaxNumber[0].CanopyVolume_max,
                ]);

                setDataCPI([
                    "CPI Total",
                    "...description for CPI Total...",
                    data.map((d: any) => d.CPITotal_value),
                    data.map((d: any) => d.CanopyArea_time ? d.CanopyArea_time : d.CPITotal_time),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinNumber[0].CanopyArea_min,
                    dwBBCardMinMaxData.dataMaxNumber[0].CanopyArea_max,
                ]);
            } else {
                setDataCanopyArea(null);
                setDataCanopyVolume(null);
                setDataCPI(null);
            }
            setDataLoading(false);
        } catch (error) {
            if (!isAbortError(error)) {
                console.error('Failed to load property data:', error);
            }
            setDataCanopyArea(null);
            setDataCanopyVolume(null);
            setDataCPI(null);
        }
    };

    const _loadCanopyStatData = async (year: number, _plantprofileId: number, metric: string, definition: Definition, signal: AbortSignal) => {
        const dtStart = `${year}-01-01 00:00:00.00`;
        const dtEnd = `${year}-12-31 23:59:59.59`;
        try {
            const data = await queryDwPointCanopyStatsData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            console.log("A092425 _loadCanopyStatData data",data);
            console.log("A080224 dwBBCardMinMaxData", dwBBCardMinMaxData);
            if (data && data.length) {
                setDataCanopyHeight([
                    "Canopy Height",
                    "Canopy Height",
                    data.map((d: any) => d.height_mean),
                    data.map((d: any) => d[`${definition.name ? definition.name : 'plant'}_time`]),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinStats[0].height_mean, //dataMaxNumber[0].CanopyArea_max
                    dwBBCardMinMaxData.dataMaxStats[0].height_mean,
                ]);
                setDataCanopyVVI([
                    "Vegetation Vigor Index",
                    "Vegetation Vigor Index",
                    data.map((d: any) => d.vvi_mean),
                    data.map((d: any) => d[`${definition.name ? definition.name : 'plant'}_time`]),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinStats[0].vvi_mean,
                    dwBBCardMinMaxData.dataMaxStats[0].vvi_mean,
                ]);
                setDataCanopyPGI([
                    "Pigmentation Index",
                    "Pigmentation Index",
                    data.map((d: any) => d.pgi_mean),
                    data.map((d: any) => d[`${definition.name ? definition.name : 'plant'}_time`]),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinStats[0].pgi_mean,
                    dwBBCardMinMaxData.dataMaxStats[0].pgi_mean,
                ]);
                setDataCanopyTIR([
                    "Thermal",
                    "...description for Thermal...",
                    data.map((d: any) => d.tir_mean),
                    data.map((d: any) => d[`${definition.name ? definition.name : 'plant'}_time`]),
                    [dtStart, dtEnd],
                    null, // time min max are not available for this metric
                    null
                ]);
            } else {
                setDataCanopyHeight(null);
                setDataCanopyVVI(null);
                setDataCanopyPGI(null);
                setDataCanopyTIR(null);
            }
            setDataLoading(false);
        } catch (error) {
            if (!isAbortError(error)) {
                console.error('Failed to load canopy stats data:', error);
            }
            setDataCanopyHeight(null);
            setDataCanopyVVI(null);
            setDataCanopyPGI(null);
            setDataCanopyTIR(null);
        }
    };

    const _loadBloomData = async (year: number, _plantprofileId: number, metric: string, definition: Definition, signal: AbortSignal) => {
        const dtStart = `${year}-01-01 00:00:00.00`;
        const dtEnd = `${year}-12-31 23:59:59.59`;
        try {
            const data = await queryDwPointBloomData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            console.log("A092425 _loadBloomData data",data);
            if (data && data.length) {
                setDataBloom([
                    "Bloom Index",
                    "Bloom Index",
                    data.map((d: any) => d["Bloom Index_value"] ? d["Bloom Index_value"] : null),
                    data.map((d: any) => d["Bloom Index_time"] ? d["Bloom Index_time"] : d["Bloom Index_date"]),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinNumberBI[0]["Bloom Index_min"],
                    dwBBCardMinMaxData.dataMaxNumberBI[0]["Bloom Index_max"],
                ]);
            } else {
                setDataBloom(null);
            }
            setDataLoading(false);
        } catch (error) {
            if (!isAbortError(error)) {
                console.error('Failed to load bloom data:', error);
            }
            setDataLoading(false);
        }
    };

    /* 
    Agriglobe > Barn Owl > 1ZN
    Agriglobe > HEadwaters Investments > Block #4 ONLY

    Soil Fertility
        --> Sulfur == Soluble Sulfur == Soluble Sulfur_mean > 8.2211
    Soil Micronutrients
        Cu == Copper == Copper_mean > 2.3832
        --> Boron == Soluble Boron == Soluble Boron_mean > 0.1597

    Soil Cation Ratios
        BS == Base Saturation == Base Saturation_mean > 100
        EA == Exchangeable Acidity == Exchangeable Acidity_mean > 0
        --> Ca% == Exchangeable Calcium Percentage == Exchangeable Calcium Percentage_mean > 52.2501
        --> Mg% == Exchangeable Magnesium Percentage == Exchangeable Magnesium Percentage_mean > 38.1291
        --> K% == Exchangeable Potassium Percentage == Exchangeable Potassium Percentage_mean > 0.3898
        --> Na% == Exchangeable Sodium Percentage == Exchangeable Sodium Percentage_mean > 9.231
        
    Soil Paste
        SP == Saturation Percentage == Saturation Percentage_mean > 36.2622
        EC == Electrical Conductivity of Saturated Paste Extract == Electrical Conductivity of Saturated Paste Extract_mean > 0.2377
        Ca - sol == Soluble Calcium == Soluble Calcium_mean > 0.3065
        Mg - sol == Soluble Magnesium == Soluble Magnesium_mean > 0.0006
        K - sol == Soluble Potassium == Soluble Potassium_mean > 0.2519
        Na - sol == Soluble Sodium == Soluble Sodium_mean > 0.0031
        Salts == Soluble Salts == Soluble Salts_mean > 0.5621

    Soil Paste Ratios
        X Sol Ca - pct == NG??? Soluble Calcium | Soluble Calcium Magnesium Ratio
        X Sol Mg - pct == NG??? Soluble Magnesium
        X Sol K - pct == NG??? Soluble Potassium | Soluble Potassium Magnesium Ratio
        Sol Na - pct == Soluble Sodium Percentage == Soluble Sodium Percentage_mean > 13.7681
        SAR == Sodium Adsorption Ratio == Sodium Adsorption Ratio_mean > 0.5133

    Soil Health
        X OC == NG - "Organic Carbon" or Soil Organic Carbom" we dont have that ???
        Macro Porosity == Macroporosity == Macroporosity_mean > 30.9344
        Pore Connectivity == Macropore Connectivity (NG - "I think so") == Macropore Connectivity_mean > 16.7011

    RESPONSE
        Base Saturation_mean > 100
        Exchangeable Acidity_mean > 0
        BD_mean > 1.5478
        CEC_mean > 16.9106
        Calcium_mean > 2674.8631
        Copper_mean > 2.3832
        FC_mean > 18.3928
        Iron_mean > 6.1032
        KSAT_mean > 2.0465
        Macropore Connectivity_mean > 16.7011
        Macroporosity_mean > 30.9344
        Magnesium_mean > 297.5146
        Manganese_mean > 19.5258
        Moisture_mean > 10.506
        Nitrogen_mean > 6.0861
        Organic Matter_mean > 0.6312
        PAWC_mean > 8.4129
        PAW_mean > 9.98
        PWP_mean > 9.98
        Phosphorus_mean > 10.0724
        Potassium_mean > 406.4009
        Saturation Percentage_mean > 36.2622
        Soluble Calcium_mean > 0.3065
        Soluble Magnesium_mean > 0.0006
        Soluble Potassium_mean > 0.2519
        Soluble Salts_mean > 0.5621
        Soluble Sodium_mean > 0.0031
        Zinc_mean > 0.2106
        bottom > 10
        pH_mean > 6.2985
        plant_id > "3558017"
        plant_name > "2782"
        plant_time > "2024-07-01T00:00:00.000Z"
        top > 0
        Sodium Adsorption Ratio_mean > 0.5133
        Soluble Sodium Percentage_mean > 13.7681
        Exchangeable Calcium Percentage_mean > 52.2501
        Exchangeable Magnesium Percentage_mean > 38.1291
        Exchangeable Potassium Percentage_mean > 0.3898
        Exchangeable Sodium Percentage_mean > 9.231
        Soluble Boron_mean > 0.1597
        Soluble Sulfur_mean > 8.2211

    */

    const _loadSoilStatData = async (year: number, _plantprofileId: number, metric: string, definition: Definition, signal: AbortSignal) => {
        const dtStart = `${year}-01-01 00:00:00.00`;
        const dtEnd = `${year}-12-31 23:59:59.59`;
        console.log("A112024 definition", definition);
        //definition.property.forEach((prop) => {
            //console.log("A112024", prop.name," -- ",prop.property);
            //console.log("A112024", prop.property);
            //console.log("A112024 -----|", prop.name);
        //});
        console.log("A091725 _loadSoilStatData ||||||===============> year, _plantprofileId, metric, definition, dtStart, dtEnd", year, _plantprofileId, metric, definition, dtStart, dtEnd);
        try {
            console.log("A091725 await queryDwPointSoilStatsData ----> _plantprofileId, definition, metric, dtStart, dtEnd", _plantprofileId, definition, metric, dtStart, dtEnd);
            const dataStats = await queryDwPointSoilStatsData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            console.log("A092425 _loadSoilStatData  dataStats",dataStats);
            if (dataStats && dataStats.length) {
                let _data = dataStats.sort((a: any, b: any) => a.top - b.top);
                // Convert depth units from cm to inches (2.5 cm per inch) when preferred unit is inches
                const _lenPref = _.get(localePreferences, 'measureUnits.length.small');
                const _lenAbbr: string = typeof _lenPref === 'string' ? _lenPref : _lenPref?.abbr || 'in';
                if (_lenAbbr === 'in') {
                    _data.forEach((s: any) => {
                        if (typeof s.top === 'number') s.top = s.top / 2.5;
                        if (typeof s.bottom === 'number') s.bottom = s.bottom / 2.5;
                    });
                }
                setDataSoilStats(_data);
            }else{
                setDataSoilStats(null);
            }
            console.log("A091725 await queryDwPointSoilDRLData ----> _plantprofileId, definition, metric, dtStart, dtEnd", _plantprofileId, definition, metric, dtStart, dtEnd);
            /*const dataDRL = await queryDwPointSoilDRLData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            if (dataDRL) {
                setDataSoilDRLStats(dataDRL);
            }else{
                setDataSoilDRLStats(null);
            }*/
            const dataCultivar = await queryDwPointCultivarStatus(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            if (dataCultivar && dataCultivar.length) {
                setDataCultivarStatus(dataCultivar);
            }else{
                setDataCultivarStatus(null);
            }
        } catch (error) {
            if (!isAbortError(error)) {
                console.error('Failed to load soil stats data:', error);
            }
            setDataSoilStats(null);
            //setDataSoilDRLStats(null);
            setDataCultivarStatus(null);
        }
        try {
            console.log("A091725 await queryDwPointSoilStatsData2025 ----> _plantprofileId, definition, metric, dtStart, dtEnd", _plantprofileId, definition, metric, dtStart, dtEnd);
            const dataStats = await queryDwPointSoilStatsData2025(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            console.log("A082225 dataStats", dataStats);
            if (dataStats) {
                let _data = dataStats.sort((a: any, b: any) => a.top - b.top);
                // Convert depth units from cm to inches (2.5 cm per inch) when preferred unit is inches
                const _lenPref = _.get(localePreferences, 'measureUnits.length.small');
                const _lenAbbr: string = typeof _lenPref === 'string' ? _lenPref : _lenPref?.abbr || 'in';
                if (_lenAbbr === 'in') {
                    _data.forEach((s: any) => {
                        if (typeof s.top === 'number') s.top = s.top / 2.5;
                        if (typeof s.bottom === 'number') s.bottom = s.bottom / 2.5;
                    });
                }
                if (_data && _data.length){
                    setDataSoilStats2025(_data);
                }else{
                    setDataSoilStats2025(false);
                }
            }
            const dataDRL = await queryDwPointSoilDRLData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            if (dataDRL) {
                setDataSoilDRLStats(dataDRL);
            }else{
                setDataSoilDRLStats(null);
            }
            const dataCultivar = await queryDwPointCultivarStatus(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            if (dataCultivar) {
                setDataCultivarStatus(dataCultivar);
            }else{
                setDataCultivarStatus(null);
            }
        } catch (error) {
            if (!isAbortError(error)) {
                console.error('Failed to load soil stats data:', error);
            }
            setDataSoilDRLStats(null);
            setDataSoilStats2025(false);
        }
    };

    return (
        <Widget
            title={`Row: ${plantProfile?.plantprofileRow}${plantProfile?.plantprofileCol ? ",  Plant: " : ""}${plantProfile?.plantprofileCol ? plantProfile?.plantprofileCol : ""}`}
            subTitle={`Id: ${plantProfile?.plantprofileId}${plantProfile?.plantprofileName ? ",  Name: " : ""}${plantProfile?.plantprofileName ? plantProfile?.plantprofileName : ""}`}
            cultivar={dataCultivarStatus && dataCultivarStatus[0] && dataCultivarStatus[0].Cultivar_name ? dataCultivarStatus[0].Cultivar_name : ""}
            status={dataCultivarStatus && dataCultivarStatus[0] && dataCultivarStatus[0].status_name ? dataCultivarStatus[0].status_name : ""}
            renderActions={() => <></>}
        >
            <div
                className="d-flex"
                style={{ paddingBottom: "20px" }}
            >
                <Form.Control as="select"
                    style={{ fontSize: "16pt", paddingLeft: "0px", fontWeight: "bold" }}
                    size="sm"
                    onChange={(e: any) => onTopCellTypeChange(e)}
                    value={selectedTopCell ? selectedTopCell : undefined}>
                    {topCellOptions.map((source: any, index: number) => (
                        <option key={`${source.id}_${index}`} value={source.id}>{source.name}</option>
                    ))
                    }
                </Form.Control>
            </div>
            {(selectedTopCell === "Canopy")
                ? (!dataCanopyArea && !dataCanopyHeight && !dataCanopyVolume && !dataCanopyVVI && !dataCanopyPGI && !dataCanopyTIR && !dataCPI)
                    ? <>
                        <div className="text-muted text-sm mt-2">Data unavailable for this date.</div>
                    </>
                    : <AboveWidgetImagery
                        loading={dataLoading}
                        metric={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].name}
                        description={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].description}
                        rasterDataSources={rasterDataSources}
                        selectedPlantObj={selectedPlantObj}
                        selectedField={selectedField}
                        bbCardDataSources={bbCardDataSources}
                        bbCardDataCounter={bbCardDataCounter}
                        setBbCardDataCounter={setBbCardDataCounter}
                        dataCanopyArea={dataCanopyArea}
                        dataCanopyHeight={dataCanopyHeight}
                        dataCanopyVolume={dataCanopyVolume}
                        propertyUnitsData={propertyUnitsData}
                        dateArrayIndexSelected={dateArrayIndexSelected}
                        dateArrayDateStringSelected={dateArrayDateStringSelected}
                        dataCanopyVVI={dataCanopyVVI}
                        dataCanopyPGI={dataCanopyPGI}
                        dataCanopyTIR={dataCanopyTIR}
                        dataCPI={dataCPI}
                        mapLayers={mapLayers}
                    />
                :<></>
            }{(selectedTopCell === "CanopySize") &&
                <AboveWidget3SparkCell
                    loading={dataLoading}
                    metric={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].name}
                    description={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].description}
                    dataSpark1={dataCanopyArea}
                    dataSpark2={dataCanopyHeight}
                    dataSpark3={dataCanopyVolume}
                    propertyUnitsData={propertyUnitsData}
                    dateSelected={dateSelected}
                />
            }{
            }{(selectedTopCell === "BloomIndex") &&
                <AboveWidget3SparkCell
                    loading={dataLoading}
                    metric={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].name}
                    description={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].description}
                    dataSpark1={dataBloom}
                    dataSpark2={null}
                    dataSpark3={null}
                    propertyUnitsData={propertyUnitsData}
                    dateSelected={dateSelected}
                />
            }{
                (selectedTopCell === "CanopyHealth") &&
                <AboveWidget3SparkCell
                    loading={dataLoading}
                    metric={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].name}
                    description={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].description}
                    dataSpark1={dataCanopyVVI}
                    dataSpark2={dataCanopyPGI}
                    dataSpark3={dataCanopyTIR}
                    propertyUnitsData={propertyUnitsData}
                    dateSelected={dateSelected}
                />
            }
            <hr />
            <div
                className="d-flex"
                style={{ paddingBottom: "20px" }}
            >
                <Form.Control
                    as="select"
                    style={{ 
                        fontSize: Array.isArray(bottomCellOptions2025) && bottomCellOptions2025.length > 0 ? "16pt" : "12pt", 
                        paddingLeft: "0px", 
                        fontWeight: Array.isArray(bottomCellOptions2025) && bottomCellOptions2025.length > 0 ? "bold" : "normal",
                        backgroundColor: Array.isArray(bottomCellOptions2025) && bottomCellOptions2025.length > 0 ? "transparent" : "transparent" }}
                    size="sm"
                    onChange={(e: any) => onBottomCellTypeChange(e)}
                    value={
                        Array.isArray(bottomCellOptions2025) && bottomCellOptions2025.length > 0
                            ? (selectedBottomCell || bottomCellOptions2025[0].id)
                            : "no-data"
                    }
                    disabled={!bottomCellOptions2025 || bottomCellOptions2025.length === 0}
                >
                    {Array.isArray(bottomCellOptions2025) && bottomCellOptions2025.length > 0 ? (
                        bottomCellOptions2025.map((source: any, index: number) => (
                            <option key={`${source.id}_${index}`} value={source.id}>
                                {source.name}
                            </option>
                        ))
                    ) : (
                        <option value="no-data">{`Options unavailable for this plant on ${dateSelected}`}</option>
                    )}
                </Form.Control>
            </div>
            {//((selectedBottomCell === "SoilFertility") || (selectedBottomCell === "SoilPaste") || (selectedBottomCell === "SoilPasteRatios") || (selectedBottomCell === "SoilCationRatios") || (selectedBottomCell === "SoilMicronutrients") || (selectedBottomCell === "SoilHealth")) &&
            (selectedBottomCell !== "SoilWater")
                ? (!dataSoilStats && !dataSoilStats2025 && !dataSoilDRLStats)
                    ? <>
                        <div className="text-muted text-sm mt-2">Data unavailable for this date.</div>
                    </>
                    : <AboveWidgetSoilFertilityCell
                        definition={definitionState}
                        loading={dataLoading}
                        metric={bottomCellOptions2025?.filter((d: any) => d.id === selectedBottomCell)[0]?.name}
                        description={bottomCellOptions2025?.filter((d: any) => d.id === selectedBottomCell)[0]?.description}
                        dataSoilStats={dataSoilStats}
                        dataSoilStats2025={dataSoilStats2025}
                        dataSoilDRLStats={dataSoilDRLStats}
                        propertyUnitsData={propertyUnitsData}
                        setDataSoilStats={setDataSoilStats}
                        bbCardByCategory={bbCardByCategory}
                    />
                :<></>
            }{
                (selectedBottomCell === "SoilWater")
                ? (!dataSoilStats)
                    ? <>
                        <div className="text-muted text-sm mt-2">Data unavailable for this date.</div>
                    </>
                    : <AboveWidgetSoilWaterCell
                        definition={definitionState}
                        loading={dataLoading}
                        metric={bottomCellOptions2025?.filter((d: any) => d.id === selectedBottomCell)[0]?.name}
                        description={bottomCellOptions2025?.filter((d: any) => d.id === selectedBottomCell)[0]?.description}
                        dataSoilStats={dataSoilStats}
                        dataSoilDRLStats={dataSoilDRLStats}
                        propertyUnitsData={propertyUnitsData}
                    />
                :<></>
            }
        </Widget>
    );
};

export default AboveWidget;
